import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { SiteLayout } from "@/components/SiteLayout";
import { MemberShell } from "@/components/MemberShell";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/lib/i18n";
import { useMember } from "@/lib/member";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";

const EMPTY = { label: "", address: "", area: "", city: "", maps_link: "" };

export default function MemberAddresses() {
  const { t } = useI18n();
  const { member, loading } = useMember();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/member/addresses").then((r) => setRows(r.data)).catch((e) => logError("Addresses failed", e));
  useEffect(() => { if (member) load(); }, [member]);

  if (loading) return <SiteLayout><div className="mx-auto max-w-4xl px-5 py-24 text-ink-soft">{t("loading")}</div></SiteLayout>;
  if (!member) return <Navigate to="/member/login" replace />;

  const save = async () => {
    try {
      await api.post("/member/addresses", form);
      setForm(EMPTY); setAdding(false); load();
      toast.success(t("addressSaved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("memberGenericError"));
    }
  };
  const remove = async (id) => { await api.delete(`/member/addresses/${id}`); load(); };

  return (
    <MemberShell>
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-2xl tracking-tight">{t("savedAddresses")}</h2>
        <button data-testid="add-address-btn" onClick={() => setAdding(!adding)}
          className="h-10 px-4 rounded-full border border-black/15 text-sm flex items-center gap-1.5 hover:border-lm-red">
          <Plus size={14} /> {t("addAddress")}
        </button>
      </div>

      {adding && (
        <div className="mt-5 lm-card rounded-xl p-5 space-y-3" data-testid="address-form">
          <Input data-testid="addr-label" placeholder={t("addrLabel")} value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })} />
          <Input data-testid="addr-address" placeholder={t("addrFull")} value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Input data-testid="addr-area" placeholder={t("addrArea")} value={form.area}
              onChange={(e) => setForm({ ...form, area: e.target.value })} />
            <Input data-testid="addr-maps" placeholder="Google Maps link" value={form.maps_link}
              onChange={(e) => setForm({ ...form, maps_link: e.target.value })} />
          </div>
          <button data-testid="addr-save" onClick={save}
            className="h-10 px-6 rounded-full bg-ink text-cream text-sm hover:bg-lm-red transition-colors">{t("save")}</button>
        </div>
      )}

      <div className="mt-5 space-y-3" data-testid="address-list">
        {rows.map((a) => (
          <div key={a.id} className="lm-card rounded-xl p-4 flex items-start gap-3" data-testid={`address-${a.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">{a.label || t("addrLabel")}</div>
              <div className="text-xs text-ink-soft mt-0.5">{a.address}{a.area ? ` · ${a.area}` : ""}</div>
            </div>
            <button data-testid={`address-del-${a.id}`} onClick={() => remove(a.id)} className="text-ink-soft hover:text-lm-red">
              <Trash2 size={15} />
            </button>
          </div>
        ))}
        {rows.length === 0 && <div className="text-sm text-ink-soft" data-testid="no-addresses">{t("noAddresses")}</div>}
      </div>
    </MemberShell>
  );
}
